import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function CategoriesSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section id="categories" className="py-16 md:py-20 bg-background-50">
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <div className="relative inline-block" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="inline-flex items-center gap-2 px-6 py-3 border-2 border-foreground-200 rounded-full text-sm font-semibold text-foreground-700 hover:border-accent-300 hover:text-accent-600 transition-colors whitespace-nowrap mb-8"
            >
              All Categories
              <i className={`${dropdownOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} transition-transform duration-200`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 max-w-[calc(100vw-2rem)] bg-white border border-background-200 rounded-2xl shadow-lg overflow-hidden z-50">
                <div className="py-2 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-5 py-3 text-sm text-foreground-700 hover:bg-accent-50 hover:text-accent-700 transition-colors"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500">
                        <i className={`${cat.icon} text-base`}></i>
                      </span>
                      <span className="font-medium">{cat.name}</span>
                      <span className="ml-auto text-xs text-foreground-400">{cat.itemCount}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-3">
            Explore Product Categories
          </h2>
          <p className="text-sm md:text-base text-foreground-500 max-w-lg mx-auto">
            Browse our organized spreadsheet categories and discover products across fashion, accessories, lifestyle and more.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {categories.map((cat) => (
            <a
              key={cat.id}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group bg-white border border-background-200 rounded-xl p-5 hover:border-accent-200 hover:bg-accent-50/30 transition-all duration-200"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 text-foreground-500 group-hover:bg-accent-100 group-hover:text-accent-600 transition-colors mb-3">
                <i className={`${cat.icon} text-lg`}></i>
              </div>
              <h3 className="font-medium text-sm text-foreground-800 mb-1 leading-snug">{cat.name}</h3>
              <p className="text-xs text-foreground-400 mb-3 leading-relaxed">{cat.description}</p>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-500 text-background-50 rounded-full text-xs font-semibold group-hover:bg-primary-600 transition-colors whitespace-nowrap">
                View
                <i className="ri-arrow-right-line text-[10px]"></i>
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}